import React, { useState } from 'react'
import { validateDateOfBirth } from '../utils/validation'
import { supabase } from '../config/supabase'
import DateOfBirthPicker from './DateOfBirthPicker'

const AgeCompletionModal = ({ user, onComplete, isOpen }) => {
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate date of birth
    const dobValidation = validateDateOfBirth(dateOfBirth, { minAge: 0, required: true })
    if (!dobValidation.isValid) {
      setError(dobValidation.message)
      return
    }

    setIsSubmitting(true)

    try {
      // Update profile with date of birth
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          date_of_birth: dateOfBirth,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)

      if (updateError) {
        console.error('Error updating profile with date of birth:', updateError)
        setError('Failed to save date of birth. Please try again.')
        setIsSubmitting(false)
        return
      }

      // Also update user metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          date_of_birth: dateOfBirth,
        },
      })

      if (metadataError) {
        console.warn('Warning: Could not update user metadata:', metadataError)
        // Don't fail if metadata update fails - profile update succeeded
      }

      // Call onComplete callback to refresh profile and close modal
      onComplete()
    } catch (err) {
      console.error('Unexpected error saving date of birth:', err)
      setError('An unexpected error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  // Calculate max date (must be at least 13 years old)
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mb-4">
            <i className="fas fa-birthday-cake text-3xl text-primary"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Complete Your Profile
          </h2>
          <p className="text-gray-600">
            Please provide your date of birth to continue. You must be at least 13 years old to use this service.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
          <DateOfBirthPicker
            id="age-completion-dateOfBirth"
            label="Date of Birth"
            value={dateOfBirth}
            onChange={(nextValue) => {
              setDateOfBirth(nextValue)
              setError('')
            }}
            error={error}
            required
          />
          <p className="mt-2 text-xs text-gray-500">
            <i className="fas fa-info-circle mr-1"></i>
            You must be at least 13 years old to use this service.
          </p>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isSubmitting || !dateOfBirth}
              className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AgeCompletionModal
